import React, { SetStateAction, useEffect, useState } from 'react';
import { jsonAxios } from 'components/axios';
import { ReactComponent as Close } from 'assets/common/close.svg'
import { AnimatePresence, motion } from 'framer-motion';
import { IDetailData, IFileInfo, ISubData, ITotal } from 'types/quote';
import QuoteDetailTable from './QuoteDetailTable';
import { download, pointCountry } from 'components/function';
import { ReactComponent as Down } from 'assets/common/download.svg'
import { ReactComponent as Down2 } from 'assets/header/download.svg'
import { ReactComponent as File } from 'assets/header/file.svg'
import CustomButton from '../CustomButton';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

interface IQuoteDetail {
    isOpen: boolean,
    setIsOpen: React.Dispatch<SetStateAction<boolean>>,
    select: {
        quoteId: number,
        optionId: number,
    }
}

const QuoteDetail = ({ isOpen, setIsOpen, select }: IQuoteDetail) => {
    const [data, setData] = useState<IDetailData>({} as IDetailData)
    const [total, setTotal] = useState<ITotal[]>([]);

    useEffect(() => {
        if (isOpen) {
            jsonAxios.get(`quote/${select.quoteId}/option/${select.optionId}`).then(res => {
                console.log(res.data)
                if (res.data.code === 200) {
                    const data: IDetailData = res.data.data
                    data.freight.map(arr => arr.price = arr.quantity * arr.unitPrice)
                    data.surcharge.map(arr => arr.price = arr.quantity * arr.unitPrice)
                    data.additional.map(arr => arr.price = arr.quantity * arr.unitPrice)
                    setData(res.data.data)
                    setTotal(calcTotalCurrency(data.freight, data.surcharge, data.additional))
                }
            })
        }

        const handleEsc = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                if (isOpen) {
                    setIsOpen(false)
                }
            }
        }

        document.addEventListener('keydown', handleEsc)

        // cleanup
        return () => {
            document.removeEventListener('keydown', handleEsc)
        }
    }, [isOpen])

    const calcTotalCurrency = (
        freight?: ISubData[],
        surcharge?: ISubData[],
        additional?: ISubData[]
    ): ITotal[] => {
        const merged = [
            ...(freight ?? []),
            ...(surcharge ?? []),
            ...(additional ?? [])
        ];

        const map = merged.reduce((acc, item) => {
            const { currency, price } = item;
            if (!currency || price == null || isNaN(price)) return acc; // 유효성 체크
            acc.set(currency, (acc.get(currency) || 0) + Number(price));
            return acc;
        }, new Map<string, number>());

        const priority = ['USD', 'KRW', 'EUR'];

        return Array.from(map, ([currency, price]) => ({ currency, price }))
            .sort((a, b) => {
                const indexA = priority.indexOf(a.currency.toUpperCase());
                const indexB = priority.indexOf(b.currency.toUpperCase());
                return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
            }).filter(arr => !arr.currency.includes('Cost'))
    };

    const downloadAllFilesAsZip = async (files: any, zipName = 'files.zip') => {
        const zip = new JSZip();
        const flatList: IFileInfo[] = files

        try {
            const fetchFile = async (file: IFileInfo, idx: number) => {
                const response = await fetch(file.filePath);
                const blob = await response.blob();
                zip?.file(`${idx + 1}_${file.fileName}`, blob); // 파일 추가
            };

            await Promise.all(flatList.map(fetchFile));

            const content = await zip.generateAsync({ type: 'blob' });
            saveAs(content, zipName);
        } catch (err) {
            console.log(err)
        }
    };


    if (!isOpen) return null;

    return (
        <div className="relative">
            <AnimatePresence>
                <motion.div
                    className="z-50 fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 tablet:px-4"
                    onClick={() => setIsOpen(false)}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <motion.div
                        className="flex flex-col gap-10 tablet:gap-4 bg-white shadow-lg tablet:p-6 px-10 py-10 rounded-lg w-[1200px] tablet:w-full max-h-[80vh] tablet:max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                    >
                        <div className='flex justify-between items-center font-semibold'>
                            <p className="tablet:text-lg text-3xl">
                                견적서 <span className='ml-6 font-normal text-[#9D9EB7]'>{data.optionName}</span>
                            </p>
                            <Close className='cursor-pointer' onClick={() => setIsOpen(false)} />
                        </div>

                        <div className='p-10 tablet:p-4 border border-[#9D9EB7] rounded'>
                            <p className='font-semibold tablet:text-base text-lg'>
                                <span dangerouslySetInnerHTML={{ __html: pointCountry(data.originCode) }}></span>
                                {' '}| {data.originAddress} &gt; {' '}
                                <span dangerouslySetInnerHTML={{ __html: pointCountry(data.destinationCode) }}></span>
                                {' '}| {data.destinationAddress}
                            </p>
                            <div className='flex justify-between mb-6 pb-6 border-[#9D9EB7] border-b text-[#9D9EB7]'>
                                <div>
                                    {
                                        data.containerInfo?.length > 0 ?
                                            (
                                                data.containerInfo.map((item, idx) => (
                                                    <p key={idx}>
                                                        {item.containerType} {item.containerWeight}KG * {item.containerQuantity}
                                                    </p>
                                                ))
                                            )
                                            :
                                            <></>
                                    }
                                </div>
                            </div>
                            <div className='flex tablet:flex-col justify-between items-center tablet:items-start font-semibold tablet:text-base text-lg'>
                                <p>{data.carrierName}</p>
                                <div className='flex gap-4'>
                                    {
                                        total.map((item, idx) => (
                                            <div
                                                className='flex gap-2 font-semibold text-lg'
                                                key={idx}>
                                                <p>{item.currency}</p>
                                                <p>{item.price?.toLocaleString()}</p>
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                        </div>
                        {
                            data?.freight?.length > 0 &&
                            <QuoteDetailTable
                                title='운임'
                                list={data.freight}
                                type={1}
                            />
                        }
                        {
                            data?.surcharge?.length > 0 &&
                            <QuoteDetailTable
                                title='부가요금'
                                list={data.surcharge}
                                type={2}
                            />
                        }
                        {
                            data?.additional?.length > 0 &&
                            <QuoteDetailTable
                                title='부대비용'
                                list={data.additional}
                                type={3}
                            />
                        }
                        {
                            data?.fileList?.length > 0 &&
                            <div className='flex flex-col gap-3 p-10 tablet:p-4 border border-[#9D9EB7] rounded-lg'>
                                <p className='font-semibold text-lg'>첨부파일</p>

                                <div className='flex flex-col flex-1 gap-3'>
                                    {
                                        data.fileList.map((item, idx) => (
                                            <div
                                                className='flex justify-between items-center bg-[#F8F8FA] px-6 py-2.5 rounded-lg h-20 hover:underline cursor-pointer'
                                                onClick={() => download(item.filePath)}
                                                key={idx}
                                            >
                                                <div className='flex justify-center items-center pr-6 border-r h-full'><File /></div>
                                                <div className='pl-6 w-full text-[#9D9EB7] text-lg text-left leading-none'>
                                                    <p className='font-semibold'>{item.fileName}</p>
                                                </div>
                                                <div className='px-6'><Down2 /></div>
                                            </div>
                                        ))
                                    }
                                </div>

                                <div>
                                    {
                                        data.fileList &&
                                        data.fileList.length > 1 &&
                                        <CustomButton
                                            className='bg-[#0ACF2E] mx-auto w-30 !min-w-400 !h-10 !text-sm shrink-0'
                                            Icon={Down}
                                            title='전체 다운로드'
                                            onClick={() => {
                                                downloadAllFilesAsZip(data.fileList, `${data.optionName}-${data.carrierName}.zip`)
                                            }}
                                        />
                                    }
                                </div>
                            </div>
                        }

                        {
                            total.length > 0 &&
                            <div className='flex justify-between items-center font-semibold text-2xl'>
                                <p>합계</p>
                                <div className='flex gap-5'>
                                    {
                                        total.map((item, idx) => (
                                            <div
                                                className='flex gap-1 rounded'
                                                key={idx}>
                                                <p className='text-primary'>{item.currency}</p>
                                                <p>{item.price?.toLocaleString()}</p>
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                        }

                    </motion.div>
                </motion.div>
            </AnimatePresence>
        </div >
    );
};

export default QuoteDetail;